import { html, View } from 'rune-ts';
import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { isUndefined } from '@fxts/core';
import { SwiperOptions } from 'swiper/types';
import {
  AnimationProgress,
  ProgressTrackerOptions,
} from '@services/MarppleShop/shared/util/AnimationProgress.ts';
import { ProgressSyncable } from '@services/MarppleShop/renderApp/components/atoms/ProgressBar/ProgressSyncable';

export class SwiperChangeEvent<V extends View<object> = View> extends CustomEvent<{
  swiper: Swiper;
  active_view: V;
  views: V[];
  current_index: number;
  previous_index: number;
}> {}

export interface SwiperStyleType {
  swiper: string;
  wrapper: string;
}

export class SlideView<T extends object> extends View<T> {
  swiper?: Swiper;
}

export interface ViewOptions {
  [key: string]: any;
}

export class SwiperView<T extends object, S extends typeof SlideView<T>> extends View<T[]> {
  swiper?: Swiper;
  active_view?: InstanceType<S>;
  subviews?: InstanceType<S>[];
  _view_option: ViewOptions[];

  private progressAnimation: AnimationProgress | null = null;

  constructor(
    public options: SwiperOptions,
    public styles: SwiperStyleType,
    public SlideView: S,
    data: T[],
    ...view_option: ViewOptions[]
  ) {
    super(data);
    this._view_option = view_option;
  }

  override template() {
    const { swiper, wrapper } = this.styles;
    const slide_views = this.data.map((data) => new this.SlideView(data, ...this._view_option));

    return html`
      <div class="${swiper}">
        <div class="${this + 'Wrapper'} ${wrapper}">${slide_views}</div>
      </div>
    `;
  }

  protected override onRender() {
    this.subviews = this.subViews(this.SlideView);

    const swiper = new Swiper(this.element(), {
      modules: [Pagination, Navigation, Autoplay, ...(this.options.modules ?? [])],
      speed: 1000,
      wrapperClass: this + 'Wrapper',
      slideClass: this.SlideView.toString(),
      slideActiveClass: this.SlideView + 'Active',
      slideVisibleClass: this.SlideView + 'Visible',
      slideFullyVisibleClass: this.SlideView + 'FullyVisible',
      slideBlankClass: this.SlideView + 'Blank',
      slideNextClass: this.SlideView + 'Next',
      slidePrevClass: this.SlideView + 'Prev',
      resizeObserver: true,
      observer: true,
      autoplay: !isUndefined(this.options.autoplay)
        ? this.options.autoplay
        : {
            delay: 5000,
            pauseOnMouseEnter: true,
            waitForTransition: true,
          },
      ...this.options,
    });

    let previous_index = swiper.realIndex;
    this.active_view = this.subviews[swiper.realIndex];

    swiper.on('realIndexChange', (swiper) => {
      if (previous_index === swiper.realIndex) return;

      this.active_view = this.subviews![swiper.realIndex];

      if (this.active_view) {
        this.dispatchEvent(SwiperChangeEvent, {
          bubbles: true,
          detail: {
            swiper,
            active_view: this.active_view,
            views: this.subviews!,
            current_index: swiper.realIndex,
            previous_index,
          },
        });
      }
      previous_index = swiper.realIndex;
    });

    this.swiper = swiper;
    this.subviews.map((view) => (view.swiper = swiper));
  }

  autoplayStop() {
    if (this.swiper) {
      this.swiper.autoplay.pause();
    }
  }

  autoplayPlay() {
    if (this.swiper) {
      this.swiper.autoplay.resume();
    }
  }

  next() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  prev() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }

  /**
   * 현재 Swiper의 진행률을 계산합니다.
   * @returns 0-100 사이의 진행률 값
   */
  public calcProgress(): number {
    if (!this.swiper) return 0;

    const swiper = this.swiper;
    const slidesPerView = typeof swiper.params.slidesPerView === 'number' ? swiper.params.slidesPerView : 1;

    // 총 슬라이드 수
    const totalSlides = swiper.slides.length;

    // 현재 인덱스
    const currentIndex = swiper.activeIndex;

    // 실제 스크롤 가능한 횟수
    const maxScrollableIndex = Math.max(0, totalSlides - slidesPerView);

    // 최소 진행률 (맨 처음에도 조금 채워진 상태로 시작)
    const minProgress = 5; // 5%부터 시작

    // 슬라이드가 1개인 경우
    if (maxScrollableIndex <= 0) {
      return 100;
    }

    // 마지막 슬라이드인지 확인
    if (currentIndex >= totalSlides - slidesPerView) {
      return 100;
    }

    // 진행률 계산 (minProgress-100)
    // 맨 처음 슬라이드에서는 minProgress 값을 가지고, 마지막에서는 100%가 되도록 계산
    const progress = minProgress + (currentIndex / maxScrollableIndex) * (100 - minProgress);

    return Math.min(Math.max(minProgress, progress), 100);
  }

  /**
   * 프로그레스 바의 애니메이션 동작을 취소합니다.
   */
  public cancelProgressAnimation(): void {
    if (this.progressAnimation) {
      this.progressAnimation.cancel();
    }
  }

  /**
   * 프로그레스 애니메이션을 시작합니다.
   */
  private animateProgress(progressSyncable: ProgressSyncable): void {
    if (!this.swiper || !this.progressAnimation) return;

    // 목표 진행률 계산
    const targetProgress = this.calcProgress();

    this.progressAnimation.animateTo(targetProgress, (progress) => {
      progressSyncable.syncProgress(progress);
    });
  }

  /**
   * 프로그레스 바 업데이트를 위한 이벤트 리스너를 설정합니다.
   * @param progressSyncable 프로그레스를 동기화할 수 있는 객체
   * @param options 프로그레스 트래커 옵션
   */
  public setupProgressTracking(
    progressSyncable: ProgressSyncable,
    options: ProgressTrackerOptions = {},
  ): void {
    if (!this.swiper) return;

    // 기존 이벤트 리스너가 있다면 먼저 제거
    this.removeProgressTracking();

    // 옵션으로 AnimationProgress 업데이트
    this.progressAnimation = new AnimationProgress(0, options);

    // 초기 진행률 동기화
    progressSyncable.syncProgress(this.calcProgress());

    // 슬라이드 변경 이벤트에 프로그레스 업데이트 추가
    const slideChangeListener = () => {
      this.animateProgress(progressSyncable);
    };

    this.swiper.on('slideChange', slideChangeListener);
    this.progressTrackingListener = slideChangeListener;
  }

  /**
   * 프로그레스 트래킹을 위해 설정한 이벤트 리스너를 제거합니다.
   */
  public removeProgressTracking(): void {
    if (!this.swiper || !this.progressTrackingListener) return;

    // 진행 중인 애니메이션 취소
    if (this.progressAnimation) {
      this.progressAnimation.cancel();
    }

    // 등록된 이벤트 리스너 제거
    this.swiper.off('slideChange', this.progressTrackingListener);
    this.progressTrackingListener = null;
  }

  // 이벤트 리스너 참조 저장을 위한 속성
  private progressTrackingListener: (() => void) | null = null;
}
